// theme/presets/panelSolidity.ts — glassBg helper (continuous panel-transparency
// pref + optional accent tint).
import { clampSolidity, clampGlassTint } from '@/core/models/preferences';
import { hexToRgb } from './color';

// Base RGB values back the translucent panel fill at the default opacity in both
// themes: dark → 11,16,21 (near-black void); light → 255,255,255 (white). Final
// text-contrast (AA) over these fills is verified in the screenshot pass.
const BASE_RGB: Record<'dark' | 'light', [number, number, number]> = {
  dark: [11, 16, 21],
  light: [255, 255, 255],
};

/**
 * Build the glassBg rgba for a theme mode.
 *
 * @param alpha  panel solidity (clamped to the valid range) → the fill's opacity.
 * @param tint   accent-tint fraction (0 → neutral void glass; higher → mixed
 *               toward `accent`). Clamped to the glass-tint range.
 * @param accent accent hex to tint toward (required for a non-zero tint).
 *
 * With the default `tint = 0` this returns the neutral fill unchanged, so callers
 * that don't opt into tinting get the original behaviour.
 */
export function glassBg(
  mode: 'dark' | 'light',
  alpha: number,
  tint = 0,
  accent = '',
  baseHex = '',
): string {
  const a = clampSolidity(alpha);
  // `baseHex` is OPTIONAL and LAST on purpose: a positional rewrite would make every
  // existing glassBg('dark', 0.8) call silently degrade to mid-grey (hexToRgb's fallback)
  // with no typecheck error.
  const base = /^#[0-9a-fA-F]{6}$/.test(baseHex)
    ? (() => {
        const { r, g, b } = hexToRgb(baseHex);
        return [r, g, b] as [number, number, number];
      })()
    : BASE_RGB[mode];
  const [br, bg, bb] = base;
  const t = clampGlassTint(tint);
  if (t <= 0 || !accent) return `rgba(${br}, ${bg}, ${bb}, ${a})`;
  const { r, g, b } = hexToRgb(accent);
  const mixCh = (base: number, ac: number) => Math.round(base + (ac - base) * t);
  return `rgba(${mixCh(br, r)}, ${mixCh(bg, g)}, ${mixCh(bb, b)}, ${a})`;
}
